import { useState, Fragment } from "react";
import ClassNames from "Helpers/Common";
import { useOpsDoctorCoverage } from "CustomHooks/useOperations";
import { Badge, PaginationFooter, EmptyRow, TableSkeleton } from "../components";
import { formatMins } from "../format";
import { PROVIDER_COLUMNS, money, SearchBar } from "./usersShared";

/* ---------------------------- State Coverage board ------------------------ */
// Per state: eligible providers, online now, visits in range, avg wait, and a
// fragility flag (<=1 provider) — answers "where are we thin, and where does one
// departure break coverage?"
const StateCoverageBoard = ({ data, loading }) => {
  const [q, setQ] = useState("");
  const presence = data?.presenceAvailable;
  const allRows = data?.rows || [];
  const rows = q ? allRows.filter((r) => (r.state || "").toLowerCase().includes(q.toLowerCase())) : allRows;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col">
          <span className="text-lg font-medium sapphire">State coverage</span>
          <span className="text-xs text-gray-400">eligible providers per state · fragile = 1 or 0 providers</span>
        </div>
        <SearchBar placeholder="Search states…" onSearch={setQ} />
      </div>
      {loading && rows.length === 0 ? (
        <TableSkeleton rows={5} cols={6} />
      ) : (
        <div className="rounded-lg bg-white shadow-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr>
                {["State", "Providers", "Online", "Visits", "Avg wait", "Coverage"].map((h, i) => (
                  <th key={h} className={ClassNames("px-6 py-4 text-xs font-normal text-gray-400 whitespace-nowrap", i === 0 ? "text-left" : "text-right")}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && <EmptyRow colSpan={6} text="No state coverage data in this period." />}
              {rows.map((r) => (
                <tr key={r.stateId} className={r.risk ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50"}>
                  <td className="px-6 py-3 text-sm font-medium sapphire">{r.state}</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-600">{r.providers}</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-500">{presence ? r.online : "—"}</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-600">{r.visits}</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-500">{r.avgWaitMins != null ? formatMins(r.avgWaitMins) : "—"}</td>
                  <td className="px-6 py-3 text-right">
                    {r.providers === 0 ? (
                      <Badge tone="red">no provider</Badge>
                    ) : r.providers === 1 ? (
                      <Badge tone="yellow">sole provider</Badge>
                    ) : (
                      <span className="text-xs text-gray-400">ok</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* --------------------- Per-doctor leave-impact drill-down ----------------- */
const CoveragePanel = ({ data }) => {
  const perState = data?.perState || [];
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-gray-500">
        Serves <b>{data?.statesServed ?? perState.length}</b> state(s) · <b>{data?.totalVisits ?? 0}</b> visits in range
      </div>
      <div className="rounded-md border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-white">
            <tr>
              {["State", "Visits", "Share of state", "Other providers"].map((h, i) => (
                <th key={h} className={ClassNames("px-4 py-2 text-xs font-normal text-gray-400", i === 0 ? "text-left" : "text-right")}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {perState.length === 0 && <EmptyRow colSpan={4} text="No visits or coverage in this period." />}
            {perState.map((s) => (
              <tr key={s.stateId}>
                <td className="px-4 py-2 sapphire font-medium">
                  {s.state}
                  {!s.eligible && s.visits > 0 && (
                    <span className="ml-2 text-xs text-gray-400">(no longer eligible)</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-gray-600">{s.visits}</td>
                <td className="px-4 py-2 text-right text-gray-500">{s.share != null ? `${s.share}%` : "—"}</td>
                <td className="px-4 py-2 text-right">
                  {s.otherProviders === 0 ? (
                    <Badge tone="red">0 · sole cover</Badge>
                  ) : s.otherProviders === 1 ? (
                    <Badge tone="yellow">1 other</Badge>
                  ) : (
                    <span className="text-gray-500">{s.otherProviders} others</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const pct = (v) => (v == null ? "—" : `${v}%`);

const MetricCell = ({ label, value, tone }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs uppercase tracking-wide text-gray-400">{label}</span>
    <span
      className={ClassNames(
        "text-base font-semibold",
        tone === "red" ? "text-red-600" : tone === "yellow" ? "text-yellow-600" : "sapphire"
      )}
    >
      {value}
    </span>
  </div>
);

const MetricGroup = ({ title, accent, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div className={ClassNames("px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100", accent)}>
      {title}
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 px-4 py-4">{children}</div>
  </div>
);

// Quality/fraud + capacity metrics (from the list row) plus capture rate (from
// the lazy coverage fetch), grouped into two cards. Concerning values are colored.
const MetricsStrip = ({ row, captureRate }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <MetricGroup title="Quality & risk" accent="bg-red-50">
      <MetricCell label="Refund rate" value={pct(row.refundRate)} tone={row.refundRate > 5 ? "red" : undefined} />
      <MetricCell label="Rx error rate" value={pct(row.rxErrorRate)} tone={row.rxErrorRate > 5 ? "red" : undefined} />
      <MetricCell label="Cancel after accept" value={pct(row.cancelRate)} tone={row.cancelRate > 5 ? "red" : undefined} />
      <MetricCell label="Low ratings (≤2★)" value={row.lowRatings ?? 0} tone={row.lowRatings > 0 ? "yellow" : undefined} />
    </MetricGroup>
    <MetricGroup title="Capacity" accent="bg-gray-50">
      <MetricCell label="Revenue" value={money(row.revenue)} />
      <MetricCell label="Avg $/visit" value={money(row.avgRevenuePerVisit)} />
      <MetricCell label="Visits / active day" value={row.visitsPerActiveDay ?? "—"} />
      <MetricCell label="Capture rate" value={pct(captureRate)} />
    </MetricGroup>
  </div>
);

const DoctorDrilldown = ({ row, range, colSpan }) => {
  const { data, isLoading, isError } = useOpsDoctorCoverage({ doctorId: row.doctorId, range, enabled: true });
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-4 bg-gray-50">
        <div className="flex flex-col gap-4">
          <MetricsStrip row={row} captureRate={data?.captureRate} />
          {isLoading ? (
            <span className="text-sm text-gray-400">Loading coverage…</span>
          ) : isError ? (
            <span className="text-sm text-red-600">Failed to load coverage.</span>
          ) : (
            <CoveragePanel data={data} />
          )}
        </div>
      </td>
    </tr>
  );
};

/* ------------------------------ Providers table --------------------------- */
const ProvidersTable = ({ data, sort, onSort, onPage, onSearch, loading, range }) => {
  const [expandedId, setExpandedId] = useState(null);
  const rows = data?.rows || [];
  const perPage = data?.perPage || 25;
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / perPage));
  const colSpan = PROVIDER_COLUMNS.length + 1; // + expand chevron

  const SortHeader = ({ col }) => {
    const active = sort?.key === col.key;
    const arrow = active ? (sort.order === "asc" ? "▲" : "▼") : "";
    return (
      <th className="px-6 py-4 text-xs font-normal text-gray-400 align-bottom whitespace-nowrap text-left">
        {col.sortable ? (
          <button
            type="button"
            onClick={() => onSort(col.key)}
            className={ClassNames("focus:outline-none hover:text-gray-600", active && "text-blue-chill font-medium")}
          >
            {col.label} {arrow}
          </button>
        ) : (
          col.label
        )}
      </th>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col">
          <span className="text-lg font-medium sapphire">Providers</span>
          <span className="text-xs text-gray-400">expand a row for their per-state leave impact</span>
        </div>
        <SearchBar placeholder="Search providers by name…" onSearch={onSearch} />
      </div>
      {loading && rows.length === 0 ? (
        <TableSkeleton rows={6} cols={colSpan} />
      ) : (
        <div className="rounded-lg bg-white shadow-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr>
                <th className="px-4 py-4" />
                {PROVIDER_COLUMNS.map((c) => (
                  <SortHeader key={c.key} col={c} />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && <EmptyRow colSpan={colSpan} text="No providers with activity in this period." />}
              {rows.map((row) => {
                const open = expandedId === row.doctorId;
                return (
                  <Fragment key={row.doctorId}>
                    <tr className={open ? "bg-gray-50" : "hover:bg-gray-50"}>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setExpandedId(open ? null : row.doctorId)}
                          className="text-gray-400 hover:text-blue-chill focus:outline-none"
                          title={open ? "Collapse" : "Expand leave impact"}
                        >
                          {open ? "▾" : "▸"}
                        </button>
                      </td>
                      {PROVIDER_COLUMNS.map((c) => (
                        <td key={c.key} className="px-6 py-3 text-sm whitespace-nowrap text-left">
                          {c.render(row)}
                        </td>
                      ))}
                    </tr>
                    {open && <DoctorDrilldown row={row} range={range} colSpan={colSpan} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <PaginationFooter page={data?.page || 1} totalPages={totalPages} total={data?.total || 0} onPage={onPage} />
        </div>
      )}
    </div>
  );
};

const SubTab = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={ClassNames(
      "px-4 py-2 text-sm font-medium focus:outline-none",
      active ? "bg-blue-chill text-white" : "bg-white text-gray-600 hover:bg-gray-50"
    )}
  >
    {label}
  </button>
);

/* Doctors — workforce view. Two sub-views: the Providers list (rows expand into
   each doctor's per-state leave-impact) and the State Coverage board. */
export const DoctorsTab = ({ providers, stateCoverage, sort, onSort, onPage, onSearch, loading, loadingCoverage, range }) => {
  const [sub, setSub] = useState("providers");
  return (
    <div className="flex flex-col gap-4">
      <div className="flex rounded-md border border-gray-300 overflow-hidden w-max">
        <SubTab active={sub === "providers"} label="Providers" onClick={() => setSub("providers")} />
        <SubTab active={sub === "coverage"} label="State coverage" onClick={() => setSub("coverage")} />
      </div>
      {sub === "providers" ? (
        <ProvidersTable data={providers} sort={sort} onSort={onSort} onPage={onPage} onSearch={onSearch} loading={loading} range={range} />
      ) : (
        <StateCoverageBoard data={stateCoverage} loading={loadingCoverage} />
      )}
    </div>
  );
};
